'use client';

import { UseFormReturn } from 'react-hook-form';

import { useMutation, useQuery } from '@tanstack/react-query';

import { UpdatePasswordErrorResponse } from '@/entities/me/types';
import { queryClient } from '@/shared/api/query-client';
import { QUERY_KEYS } from '@/shared/const/query-keys';
import { useSession } from '@/shared/hooks/use-session';
import { useApi } from '@/shared/hooks/use-api';
import {
  ApiError,
  setFormErrorsFromApi,
} from '@/shared/utils/form/setFormApiErrors';
import { isTelegramPlaceholderEmail } from '@/shared/const/telegram-account';
import { getStrapiMedia } from '@/shared/utils/utils';
import { useToast } from '@/components/ui/use-toast';
import { createAuthenticatedRequest } from '@/shared/api/api-client';

type AuthApi = ReturnType<typeof createAuthenticatedRequest>;
export interface IUser {
  blocked: boolean;
  confirmed: boolean;
  createdAt: string;
  email: string;
  emailForSend?: string;
  id: number;
  image: string | null;
  provider: string;
  role: { id: number; name: string; description: string; type: string };
  updatedAt: string;
  username: string;
  /** Привязка Telegram (Strapi), опционально */
  telegramId?: string | null;
  telegramUsername?: string | null;
  /** Согласие на рекламную рассылку (ст. 18 ФЗ «О рекламе») */
  marketingConsent?: boolean | null;
  marketingConsentAt?: string | null;
}

const normalizeMeImage = (raw: unknown): string | null => {
  if (raw == null) return null;
  if (typeof raw === 'string') return getStrapiMedia(raw);
  if (
    typeof raw === 'object' &&
    raw !== null &&
    'url' in raw &&
    typeof (raw as { url: unknown }).url === 'string'
  ) {
    return getStrapiMedia((raw as { url: string }).url);
  }
  return null;
};

export const useMeQuery = () => {
  const { token, isPending } = useSession();
  const api = useApi();

  return useQuery({
    queryKey: [QUERY_KEYS.profile, token ?? ''],
    queryFn: async () => api.get(`/users/me?populate=*`),
    select: (axiosResponse): IUser => {
      const raw = axiosResponse.data as { data?: Record<string, unknown> } & Record<string, unknown>;
      const u = (raw?.data ?? raw) as Record<string, unknown>;
      return {
        ...(u as unknown as IUser),
        image: normalizeMeImage(u.image),
      };
    },
    enabled: !isPending && !!token,
  });
};

export const useChangePasswordMutation = (form: UseFormReturn<any>) => {
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: {
      currentPassword: string;
      password: string;
      passwordConfirmation: string;
    }) => api.post(`/auth/change-password`, data),
    onSuccess: () => {
      form.reset();
      toast({
        title: 'Пароль успешно изменен',
        variant: 'success',
      });
    },
    onError: (error_: UpdatePasswordErrorResponse) => {
      const errorMessage = error_?.response?.data?.error?.message;

      const errorMessages: Record<string, string> = {
        'The provided current password is invalid':
          'Текущий пароль введен неверно',
        'Your new password must be different than your current password':
          'Новый пароль должен отличаться от текущего',
      };

      const errors: Record<string, ApiError> = {
        'The provided current password is invalid': {
          message: errorMessages[error_.response?.data?.error?.message],
          formErrors: [
            {
              field: 'currentPassword',
              message: errorMessages[error_.response?.data?.error?.message],
            },
          ],
        },

        'Your new password must be different than your current password': {
          message: errorMessages[error_.response?.data?.error?.message],
          formErrors: [
            {
              field: 'password',
              message: errorMessages[error_.response?.data?.error?.message],
            },
            {
              field: 'passwordConfirmation',
              message: errorMessages[error_.response?.data?.error?.message],
            },
          ],
        },
      };

      setFormErrorsFromApi(form, errors[errorMessage]);

      if (errorMessage) {
        toast({
          title: errors[errorMessage].message,
          variant: 'error',
        });
      }
    },
  });
};

/** URL файла после POST /upload (Strapi + S3 в plugins.ts). */
const pickUploadFileUrl = (data: unknown): string | null => {
  if (!Array.isArray(data) || data.length === 0) return null;
  const first = data[0] as { url?: string };
  return typeof first?.url === 'string' ? first.url : null;
};

const uploadFormDataHeaders = {
  'Content-Type':
    'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
};

/** POST /upload + PUT /users/:id — общий путь для ручной смены и синка из Telegram. */
export const uploadUserAvatarFileToStrapi = async (
  api: AuthApi,
  strapiUserId: number,
  file: File,
) => {
  const formData = new FormData();
  formData.append('files', file);
  const uploadRes = await api.post(`/upload`, formData, {
    headers: uploadFormDataHeaders,
  });
  const fileUrl = pickUploadFileUrl(uploadRes.data);
  if (!fileUrl) {
    throw new Error('upload_no_url');
  }
  return api.put(`/users/${strapiUserId}`, { image: fileUrl });
};

export const useUpdateAvatarMutation = () => {
  const { user } = useSession();
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (avatar: File) => {
      const id = user?.strapiUserId;
      if (!id) {
        throw new Error('no_strapi_user');
      }
      return uploadUserAvatarFileToStrapi(api, id, avatar);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.profile] });
      toast({
        title: 'Аватар успешно обновлен',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Не удалось загрузить файл',
        variant: 'error',
      });
    },
  });
};

export const useUploadFilesMutation = () => {
  const api = useApi();

  return useMutation({
    mutationFn: async (file: File[]) => {
      const formData = new FormData();
      for (const f of file) {
        formData.append('files', f);
      }
      return api.post(`/upload`, formData, {
        headers: {
          'Content-Type':
            'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
        },
      });
    },
  });
};
/**
 * Подписка/отписка от рекламной рассылки.
 * Отписка должна срабатывать сразу — это требование ст. 18 ФЗ «О рекламе».
 */
export const useChangeMarketingConsentMutation = () => {
  const { toast } = useToast();
  const { user } = useSession();
  const api = useApi();

  return useMutation({
    mutationFn: (marketingConsent: boolean) =>
      api.put(`/users/${user?.strapiUserId}`, {
        marketingConsent,
        ...(marketingConsent && {
          marketingConsentAt: new Date().toISOString(),
        }),
      }),
    onSuccess: (_data, marketingConsent) => {
      toast({
        title: marketingConsent
          ? 'Вы подписаны на рассылку'
          : 'Вы отписаны от рассылки',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.profile] });
    },
    onError: () => {
      toast({
        title: 'Не удалось изменить подписку',
        variant: 'error',
      });
    },
  });
};

export const useChangeEmailForSendMutation = (form: UseFormReturn<any>) => {
  const { toast } = useToast();
  const { user, token } = useSession();
  const api = useApi();

  return useMutation({
    mutationFn: (data: { emailForSend: string }) => {
      const next = data.emailForSend.trim();
      const me = queryClient.getQueryData<IUser>([
        QUERY_KEYS.profile,
        token ?? '',
      ]);
      const payload: Record<string, string> = { emailForSend: next };
      if (me && isTelegramPlaceholderEmail(me.email)) {
        payload.email = next;
      }
      return api.put(`/users/${user?.strapiUserId}`, payload);
    },
    onSuccess: () => {
      // @ts-ignore
      form.setValue('emailForSend', '');
      toast({
        title: 'Email для отправки МК успешно изменен',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.profile] });
    },
    onError: () => {
      toast({
        title: 'Произошла ошибка',
        variant: 'error',
      });
    },
  });
};
